import FormContainer from "@app/components/forms/container/FormContainer";
import { useFormWithSchema } from "@app/components/forms/hooks/useFormWithSchema";
import {
  FormFieldType,
  FormFieldSchema,
} from "@app/components/forms/types/schema";
import { CommonErrorModal } from "@app/components/modals/CommonErrorModal";
import { PostingUpdateSchema } from "@app/components/modals/PostingSelectorModal/paymentMethodModalSchema";
import { TabIds } from "@app/pages/Postings";
import {
  useGetStoryGroupList,
  useUpdateStoryGroup,
  useUpdateStory,
  useGetStories,
  useUpdateStoriesMetadata,
  useUpdateStoryMetadata,
  useGetOnlyStoriesNotIncludeRelationData,
  useGetStory,
} from "@shortsworks/user-api";
import { useQueryClient } from "@tanstack/react-query";

import { forwardRef, useState, useEffect, Suspense } from "react";
import {
  Button,
  ModalContent,
  ModalProps,
  createModalComponent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
  DialogClose,
  DialogContent,
  Separator,
  useModal,
  TabsList,
  TabsTrigger,
  TabsContent,
  Tabs,
  LoadingContainer,
} from "@shortsworks/ui";
import { PostingList } from "@app/components/PostingList";

interface PostingSelectorModalProps {
  title: string;
  description: string;
  selectedIds: string[];
}

const PostingSelectorModalContent = <T = any, R = []>(
  props: ModalProps<PostingSelectorModalProps>,
) => {
  const { showModal } = useModal();
  const [selectedTab, setSelectedTab] = useState<TabIds>("posting");
  const [selectedIds, setSelectedIds] = useState<Map<TabIds, string[]>>();
  const isEmptySelectedPostings = !(
    selectedIds && [...selectedIds.values()].flat().length !== 0
  );

  const handleSelectedPostings = (ids: string[]) => {
    setSelectedIds((prev) => {
      return new Map(prev).set(selectedTab, ids);
    });
  };

  const handleChangeSelectedTab = (value: TabIds) => {
    return setSelectedTab(value);
  };

  const handleSaveSelectedPostings = () => {
    if (!selectedIds) {
      return showModal(CommonErrorModal, {
        title: "선택된 포스팅이 없습니다.",
      });
    }
    return props.close([...selectedIds.values()].flat());
  };

  return (
    <ModalContent>
      <DialogHeader>
        <DialogTitle>{props.title}</DialogTitle>
        <DialogDescription>{props.description}</DialogDescription>
      </DialogHeader>

      <Tabs
        className="flex flex-col h-full"
        defaultValue="posting"
        onValueChange={handleChangeSelectedTab}
      >
        <div className="flex flex-row items-center gap-2">
          <TabsList className="w-fit">
            <TabsTrigger value="posting">포스팅</TabsTrigger>
            <TabsTrigger value="temporary">임시 저장</TabsTrigger>
            <TabsTrigger value="hidden">숨김</TabsTrigger>
          </TabsList>
        </div>
        <TabsContent
          value="posting"
          className="data-[state='active']:flex flex-col h-64 overflow-scroll"
        >
          <LoadingContainer>
            <PostingList
              onSelect={handleSelectedPostings}
              selectedIds={selectedIds?.get("posting")}
            />
          </LoadingContainer>
        </TabsContent>
        <TabsContent
          value="temporary"
          className="data-[state='active']:flex flex-col h-64 overflow-scroll"
        >
          <LoadingContainer>
            <PostingList
              params={{ draft: true }}
              onSelect={handleSelectedPostings}
              selectedIds={selectedIds?.get("temporary")}
            />
          </LoadingContainer>
        </TabsContent>
        <TabsContent
          value="hidden"
          className="data-[state='active']:flex flex-col h-64 overflow-scroll"
        >
          <LoadingContainer>
            <PostingList
              params={{ hidden: true }}
              onSelect={handleSelectedPostings}
              selectedIds={selectedIds?.get("hidden")}
            />
          </LoadingContainer>
        </TabsContent>
      </Tabs>

      <DialogFooter>
        <DialogClose asChild></DialogClose>
        <Button
          disabled={isEmptySelectedPostings}
          onClick={handleSaveSelectedPostings}
          className="p-0 w-24 h-10"
        >
          확인
        </Button>
      </DialogFooter>
    </ModalContent>
  );
};

export const PostingSelectorModal = createModalComponent<
  PostingSelectorModalProps,
  string[]
>(PostingSelectorModalContent);

type PostingUpdateContentProps = {
  storyId: string;
  name: string;
};

const PostingUpdateContent = (props: ModalProps<PostingUpdateContentProps>) => {
  const { showModal } = useModal();

  const queryClient = useQueryClient();
  const { queryKey: getStoriesQueryKey } = useGetStories();

  const updateStoryMetadata = useUpdateStoryMetadata({
    mutation: {
      onSuccess: () => {
        return queryClient.invalidateQueries({ queryKey: getStoriesQueryKey });
      },
    },
  });

  const { form, schemas, parseForm, setFormValues } = useFormWithSchema({
    formSchemas: PostingUpdateSchema,
    mode: "onChange",
  });

  useEffect(() => {
    setFormValues({
      name: props.name,
    });
  }, []);

  const updatePosting = async () => {
    const parsedForm = parseForm();
    if (!parsedForm.success) return;
    const { name } = parsedForm.data;

    if (name === props.name) {
      return showModal(CommonErrorModal, {
        title: "포스팅이름이 현재와 동일합니다.",
      });
    }

    try {
      const result = await updateStoryMetadata.mutateAsync({
        storyId: props.storyId,
        data: { name },
      });
      if (!result.data.success) {
        return showModal(CommonErrorModal, {
          title: "포스팅 이름 변경에 실패했습니다.",
        });
      }
    } catch (error: any) {
      return showModal(CommonErrorModal, {
        title: "포스팅 이름 변경에 실패했습니다.",
      });
    }
  };

  return (
    <ModalContent>
      <DialogHeader>
        <DialogTitle>포스팅 이름 변경</DialogTitle>
        <DialogDescription>
          <FormContainer className="mt-2" schemas={schemas} form={form} />
        </DialogDescription>
      </DialogHeader>
      <DialogFooter>
        <DialogClose asChild></DialogClose>
        <Button className="p-0 w-[104px] h-[40px]" onClick={updatePosting}>
          확인
        </Button>
      </DialogFooter>
    </ModalContent>
  );
};

export const PostingUpdateModal = createModalComponent(PostingUpdateContent);

type postingGroupContentProps = {
  storyIds: string[];
  name: string;
  groupListSchema: FormFieldSchema[];
  groupIds: string[];
};

const PostingGroupChangeContent = (
  props: ModalProps<postingGroupContentProps>,
) => {
  const { showModal } = useModal();

  const queryClient = useQueryClient();
  const { queryKey: getStoriesQueryKey } = useGetStories();
  const { mutateAsync: mutateAsyncUpdateStoryMetadata } =
    useUpdateStoriesMetadata({
      mutation: {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: getStoriesQueryKey });
          return props.close();
        },
      },
    });

  const { form, schemas, parseForm, setFormValues } = useFormWithSchema({
    formSchemas: props.groupListSchema,
    mode: "onChange",
    // set defaultValue by metadata
    defaultFormValues: props.groupIds.reduce(
      (schema: Record<string, boolean>, item: string) => {
        schema[item] = true;
        return schema;
      },
      {},
    ),
  });

  const updateGroup = async () => {
    const parsedForm = parseForm();
    if (!parsedForm.success) return;

    const groupIds = Object.keys(parsedForm.data).filter(
      (key) => parsedForm.data[key] === true,
    );
    const result = await mutateAsyncUpdateStoryMetadata({
      data: {
        storyIds: props.storyIds,
        groupIds
      },
    });

    if (!result.data.success) {
      return showModal(CommonErrorModal, {
        title: "그룹 목록이 변경에 실패했습니다.",
      });
    }
  };

  return (
    <ModalContent>
      <DialogHeader>
        <DialogTitle>포스팅 그룹 변경</DialogTitle>
        <DialogDescription></DialogDescription>
      </DialogHeader>
      <Suspense>
        <FormContainer className="mt-2" schemas={schemas} form={form} />
      </Suspense>
      <DialogFooter>
        <DialogClose asChild></DialogClose>
        <Button className="p-0 w-[104px] h-[40px]" onClick={props.close}>
          취소
        </Button>
        <Button className="p-0 w-[104px] h-[40px]" onClick={updateGroup}>
          확인
        </Button>
      </DialogFooter>
    </ModalContent>
  );
};

export const PostingGroupChangeModal = createModalComponent(
  PostingGroupChangeContent,
);
